import React, { BaseHTMLAttributes } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { colors } from '@mobi/component-library/Theme/Common/colors'
import { Icon } from './Icon'

type FeatureIconProps = {
  type: 'warning' | 'success' | 'error' | 'custom' | 'custom_secondary' | 'grey'
  size: 'sm' | 'md' | 'lg' | 'xl'
} & BaseHTMLAttributes<HTMLBaseElement>

export const FeatureIcon: React.FC<FeatureIconProps> = ({
  children,
  type = 'custom',
  size = 'lg',
}) => {
  const icon = children ? children : <Icon name={iconMap[type]} size={iconSizing[size]} />
  return (
    <IconWrapper type={type} size={size}>
      {icon}
    </IconWrapper>
  )
}

const IconWrapper = styled.div<FeatureIconProps>(
  {
    display: 'flex',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ({ type = 'custom', size = 'md' }) => ({ ...wrapperColors[type], ...wrapperSizing[size] })
)

const wrapperColors: Record<FeatureIconProps['type'], CSSObject> = {
  warning: {
    backgroundColor: colors.warning[50],
    color: colors.warning[600],
  },
  success: {
    backgroundColor: colors.success[50],
    color: colors.success[600],
  },
  error: {
    backgroundColor: colors.error[50],
    color: colors.error[600],
  },
  custom: {
    backgroundColor: colors.lavender[100],
    color: colors.studio[600],
  },
  custom_secondary: {
    backgroundColor: colors.surface[100],
    color: colors.neutral[900],
  },
  grey: {
    backgroundColor: colors.neutral[200],
    color: colors.black,
  },
}

const wrapperSizing: Record<FeatureIconProps['size'], CSSObject> = {
  sm: {
    width: '2.4rem',
    height: '2.4rem',
  },
  md: {
    width: '3.2rem',
    height: '3.2rem',
  },
  lg: {
    width: '4.8rem',
    height: '4.8rem',
  },
  xl: {
    width: '6.4rem',
    height: '6.4rem',
  },
}

const iconMap: Record<FeatureIconProps['type'], React.ComponentProps<typeof Icon>['name']> = {
  success: 'LineCheckCircle',
  warning: 'LineAlertTriangle',
  error: 'LineAlertCircle',
  custom: 'LinePlaceholder',
  custom_secondary: 'LinePlaceholder',
  grey: 'LineInfoCircle',
}

const iconSizing: Record<FeatureIconProps['size'], string> = {
  sm: '1.4rem',
  md: '1.6rem',
  lg: '2.4rem',
  xl: '3.2rem',
}
